import { Dispatch } from 'react';
import { IAppointment } from '../../../../API/GoMoto';
import { GoMotoFunction } from '../../../../API/GoMoto.types';
import { QRCodeData } from '../../../../context/mzero';

/**
 * This object extends the QRCodeData object after omitting specific properties like the `apptNo` and `key` so that
 * they are not mutually exclusive properties. We then add a few additional properties specifically for the Dev tools.
 */
export interface ExtendedDevQRCodeData extends Omit<QRCodeData, 'apptNo' | 'key'> {
    /**
     * The appointment key. Redefined here to remove the mutually exclusive rule
     * with `apptNo`.
     */
    key?: QRCodeData['key'];
    /**
     * The appointment number for DMS appointments. Redefined here to remove the
     * mutually exclusive rule with `key`
     */
    apptNo?: string;
    /**
     * The appointment total we are manually adding to the QR Code in spite of whatever
     * the appointment object actually has  to override it.
     */
    total?: number;
    /**
     * The payment status that we are manually adding to the QR Code in spite of whatever
     * the appointment object actually has to override it.
     */
    paid?: IAppointment['paymentStatus'];
    /**
     * The MFA code that is send in the QR Code when logging into the admin screen.
     */
    mfaCode?: string;
}

/**
 * An enum to differentiate between a DMS appointment QR Code
 * and a non-DMS appointment QR Code.
 */
export enum DevQRType {
    /**
     * Used to set the check-in QR Code format in the dev tools to that of a DMS appointment.
     */
    DMS = 0,
    /**
     * Used to set the check-in QR Code format in the dev tools to that of a non-DMS appointment.
     */
    NonDMS
}

/**
 * An enum to differentiate between whether the dealer for this appointment
 * is a DMS Dealer or a Non DMS Dealer. This differentiation can only ever
 * apply to a DMS appointment.
 */
export enum DevQRDealerType {
    /**
     * Used to set the check-in QR Code format in the dev tools for the dealer info to include
     * the dealer key, store id, and area id.
     */
    DMSDealer = 0,
    /**
     * Used to set the check-in QR Code format in the dev tools for the dealer info to only
     * include the dealer key.
     */
    Dealer
}

/**
 * The object for the formik values that additionally adds a way for us
 * to set whether the check-in appointment is a DMS or non-DMS appointment
 * as well as whether the dealer is a DMS or non-DMS dealer.
 */
export interface ScanDevQRCodeData extends ExtendedDevQRCodeData {
    /**
     * Allows the user to set the form format for check-in QR Codes in the dev tools
     * to either a DMS or non-DMS appointment format.
     */
    dmsOrNonDMS: DevQRType;
    /**
     * Allows the user to set the form format for check-in QR Codes in the dev tools
     * to either a DMS or non-DMS dealer format.
     */
    dmsOrDealer: DevQRDealerType;
}

/**
 * The type intersection for what kind of form should be provided in the ScanQRCodeDevTools
 * component, so we can re-use it for all QR Code Dev Tool needs.
 */
export type QRCodeTypes = 'checkin' | 'pickup' | 'mfa';

/**
 * The props object that ScanQRCodeDevTools takes.
 */
export interface ScanQRCodeDevToolsProps {
    /**
     * The function to simulate that we scanned a QR Code for the hardware
     * provider.
     */
    onScan: GoMotoFunction<
        [ExtendedDevQRCodeData],
        void
    >;
    /**
     * Whether the dev tools dialog is open or closed.
     */
    isDevToolsOpen: boolean;
    /**
     * A callback to set the dev tools dialog to closed when we submit the dev tools form and
     * simulate scanning a QR Code.
     */
    setIsDevToolsOpen: Dispatch<boolean>;
    /**
     * What type of QR Code the dev tools dialog should mimic.
     */
    qrCodeType: QRCodeTypes;
}